class ImageSlider {
  constructor() {
    this.sliders = document.querySelectorAll('[data-image-slider]');
    this.init();
  }

  init() {
    if (!this.sliders.length) return;

    this.sliders.forEach((slider) => {
      const root = slider.closest('.image-slider') || slider;
      const track = slider.querySelector('.image-slider__track');
      const slides = slider.querySelectorAll('.image-slider__slide');
      const prevBtn = root.querySelector('.image-slider__nav--prev');
      const nextBtn = root.querySelector('.image-slider__nav--next');
      const openButtons = root.querySelectorAll('[data-image-slider-open]');
      const modal = root.querySelector('[data-image-slider-modal]');

      if (!track || !slides.length) return;

      this.setupDragNavigation(track);
      this.setupButtons(track, slides, prevBtn, nextBtn);

      if (openButtons.length && modal) {
        this.setupModal(root, slider, modal, openButtons, slides);
      }
    });
  }

  setupDragNavigation(track) {
    let isDragging = false;
    let startX;
    let scrollLeft;

    const getClientX = (event) => {
      if (event.type.startsWith('touch')) {
        if (event.touches && event.touches[0]) {
          return event.touches[0].clientX;
        }

        if (event.changedTouches && event.changedTouches[0]) {
          return event.changedTouches[0].clientX;
        }
      }

      return event.clientX;
    };

    const onDragStart = (event) => {
      const interactiveTarget = event.target.closest(
        'button, a, [data-image-slider-open]',
      );

      if (interactiveTarget) return;

      isDragging = true;
      startX = getClientX(event);
      scrollLeft = track.scrollLeft;

      track.classList.add('image-slider__track--dragging');
    };

    const onDragMove = (event) => {
      if (!isDragging) return;

      const x = getClientX(event);
      const walk = (startX - x) * 1.5;
      track.scrollLeft = scrollLeft + walk;
    };

    const onDragEnd = () => {
      if (!isDragging) return;

      isDragging = false;
      track.classList.remove('image-slider__track--dragging');
    };

    track.addEventListener('mousedown', onDragStart);
    track.addEventListener('mousemove', onDragMove);
    track.addEventListener('mouseup', onDragEnd);
    track.addEventListener('mouseleave', () => {
      if (!isDragging) return;

      isDragging = false;
      track.classList.remove('image-slider__track--dragging');
    });

    // Тач-обработчики: позволяем нативную прокрутку и при этом поддерживаем drag-свайп
    track.addEventListener('touchstart', onDragStart, { passive: true });
    track.addEventListener('touchmove', onDragMove, { passive: true });
    track.addEventListener('touchend', onDragEnd);
    track.addEventListener('touchcancel', onDragEnd);
  }

  setupButtons(track, slides, prevBtn, nextBtn) {
    if (!prevBtn || !nextBtn) return;

    const slidesArray = Array.from(slides);
    const activeClass = 'image-slider__slide--active';
    const getBaseOffset = () => slidesArray[0]?.offsetLeft || 0;
    const getSlideScrollLeft = (index) => {
      const slide = slidesArray[index];
      if (!slide) return 0;
      return Math.max(0, slide.offsetLeft - getBaseOffset());
    };

    const getCurrentSlideIndexByScroll = () => {
      if (slidesArray.length > 1 && getSlideScrollLeft(1) === 0) {
        return 0;
      }

      const currentScroll = track.scrollLeft;
      const tolerance = 2;
      let currentIndex = 0;

      for (let index = 0; index < slidesArray.length; index += 1) {
        if (getSlideScrollLeft(index) <= currentScroll + tolerance) {
          currentIndex = index;
        } else {
          break;
        }
      }

      return currentIndex;
    };

    const setActiveSlide = (index) => {
      slidesArray.forEach((slide, slideIndex) => {
        slide.classList.toggle(activeClass, slideIndex === index);
      });
    };

    const updateActiveSlide = () => {
      if (!slidesArray.length) return;
      const currentIndex = getCurrentSlideIndexByScroll();
      setActiveSlide(currentIndex);
    };

    const getActiveIndex = () => {
      const index = slidesArray.findIndex((slide) => slide.classList.contains(activeClass));
      return index >= 0 ? index : 0;
    };

    const scrollToSlide = (index) => {
      const targetIndex = Math.max(0, Math.min(slidesArray.length - 1, index));
      const targetSlide = slidesArray[targetIndex];

      if (!targetSlide) return;

      const fromLeft = track.scrollLeft;
      const toLeft = getSlideScrollLeft(targetIndex);
      const delta = toLeft - fromLeft;

      setActiveSlide(targetIndex);

      track.scrollTo({
        left: toLeft,
        behavior: 'smooth',
      });
    };

    prevBtn.addEventListener('click', () => {
      const currentIndex = getActiveIndex();
      scrollToSlide(currentIndex - 1);
    });

    nextBtn.addEventListener('click', () => {
      const currentIndex = getActiveIndex();
      scrollToSlide(currentIndex + 1);
    });

    track.addEventListener('scroll', updateActiveSlide, { passive: true });
    updateActiveSlide();
  }

  setupModal(root, slider, modal, openButtons, slides) {
    const closeElements = modal.querySelectorAll('[data-image-slider-close]');
    const thumbButtons = modal.querySelectorAll('[data-image-slider-thumb]');
    const slideNodes = modal.querySelectorAll('[data-image-slider-modal-slide]');
    const prevBtn = modal.querySelector('[data-image-slider-modal-prev]');
    const nextBtn = modal.querySelector('[data-image-slider-modal-next]');
    const thumbsTrack = modal.querySelector('[data-image-slider-modal-thumbs-track]');
    const thumbsPrev = modal.querySelector('[data-image-slider-modal-thumbs-prev]');
    const thumbsNext = modal.querySelector('[data-image-slider-modal-thumbs-next]');
    const main = modal.querySelector('.image-slider-modal__main');

    if (!slideNodes.length) return;

    let currentIndex = 0;
    let lastFocusedElement = null;

    const setActive = (index) => {
      const safeIndex = Math.max(0, Math.min(slideNodes.length - 1, index));
      currentIndex = safeIndex;

      slideNodes.forEach((slide, slideIndex) => {
        slide.classList.toggle('image-slider-modal__slide--active', slideIndex === safeIndex);
      });

      thumbButtons.forEach((thumb) => {
        const thumbIndex = Number(thumb.getAttribute('data-image-slider-thumb'));
        thumb.classList.toggle('image-slider-modal__thumb--active', thumbIndex === safeIndex);
      });

      if (thumbsTrack) {
        const activeThumb = thumbsTrack.querySelector('[data-image-slider-thumb].image-slider-modal__thumb--active');

        if (activeThumb) {
          const { offsetTop, offsetHeight } = activeThumb;
          const { scrollTop, clientHeight } = thumbsTrack;

          if (offsetTop < scrollTop) {
            thumbsTrack.scrollTop = offsetTop;
          } else if (offsetTop + offsetHeight > scrollTop + clientHeight) {
            thumbsTrack.scrollTop = offsetTop + offsetHeight - clientHeight;
          }
        }
      }
    };

    if (main) {
      let touchStartX = 0;
      let touchEndX = 0;
      const swipeThreshold = 50;

      const onTouchStart = (event) => {
        if (!event.touches || event.touches.length !== 1) return;
        touchStartX = event.touches[0].clientX;
        touchEndX = touchStartX;
      };

      const onTouchMove = (event) => {
        if (!event.touches || event.touches.length !== 1) return;
        touchEndX = event.touches[0].clientX;
      };

      const onTouchEnd = () => {
        const diffX = touchEndX - touchStartX;

        if (Math.abs(diffX) < swipeThreshold) {
          return;
        }

        if (diffX < 0) {
          setActive(currentIndex + 1);
        } else {
          setActive(currentIndex - 1);
        }

        touchStartX = 0;
        touchEndX = 0;
      };

      main.addEventListener('touchstart', onTouchStart, { passive: true });
      main.addEventListener('touchmove', onTouchMove, { passive: true });
      main.addEventListener('touchend', onTouchEnd);
      main.addEventListener('touchcancel', onTouchEnd);
    }

    const open = (initialIndex) => {
      if (modal.classList.contains('image-slider-modal--open')) return;

      lastFocusedElement = document.activeElement;
      modal.classList.add('image-slider-modal--open');
      modal.setAttribute('aria-hidden', 'false');

      if (typeof initialIndex === 'number' && Number.isFinite(initialIndex)) {
        setActive(initialIndex);
      } else {
        const mainOffsets = Array.from(slides).map((slide) => slide.offsetLeft);
        const currentScroll = slider.querySelector('.image-slider__track')?.scrollLeft || 0;

        if (mainOffsets.length) {
          let nearestIndex = 0;
          let minDiff = Infinity;

          mainOffsets.forEach((offset, index) => {
            const diff = Math.abs(offset - currentScroll);

            if (diff < minDiff) {
              minDiff = diff;
              nearestIndex = index;
            }
          });

          setActive(nearestIndex);
        } else {
          setActive(0);
        }
      }
    };

    const close = () => {
      if (!modal.classList.contains('image-slider-modal--open')) return;

      modal.classList.remove('image-slider-modal--open');
      modal.setAttribute('aria-hidden', 'true');

      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    };

    const slidesArray = Array.from(slides);
    openButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const slideElement = button.closest('.image-slider__slide');
        const index = slidesArray.indexOf(slideElement);
        if (index >= 0) {
          open(index);
        } else {
          open();
        }
      });
    });

    closeElements.forEach((element) => {
      element.addEventListener('click', close);
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        setActive(currentIndex - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        setActive(currentIndex + 1);
      });
    }

    thumbButtons.forEach((button) => {
      const activateFromThumb = () => {
        const index = Number(button.getAttribute('data-image-slider-thumb'));
        setActive(index);
      };

      button.addEventListener('click', activateFromThumb);
      button.addEventListener('mouseenter', activateFromThumb);
      button.addEventListener('focus', activateFromThumb);
    });

    if (thumbsPrev && thumbsTrack) {
      thumbsPrev.addEventListener('click', () => {
        thumbsTrack.scrollBy({
          top: -thumbsTrack.clientHeight / 2,
          behavior: 'smooth',
        });
      });
    }

    if (thumbsNext && thumbsTrack) {
      thumbsNext.addEventListener('click', () => {
        thumbsTrack.scrollBy({
          top: thumbsTrack.clientHeight / 2,
          behavior: 'smooth',
        });
      });
    }
  }
}

new ImageSlider();